/**
 * Mapeia Schedule FHIR (+ Locations incluídas) → ProntoAtendimentoConfig.
 */
import { attendanceTypeToMode } from "./buildProntoAtendimentoSchedule";
import {
  PRONTO_ATENDIMENTO_DEFAULT_EXTENSION_URL,
  PRONTO_ATENDIMENTO_SERVICE_CATEGORY,
  SCHEDULE_ATTENDANCE_TYPE_EXTENSION_URL,
  SERVICE_COST_EXTENSION_URL,
} from "./serviceCategory";
import type { ProntoAtendimentoConfig } from "./types";

/** Confirma se o Schedule é de Pronto Atendimento (filtro client-side de segurança). */
export function isProntoAtendimentoSchedule(
  schedule: fhir5.Schedule | undefined,
): boolean {
  if (!schedule?.serviceCategory?.length) return false;
  return schedule.serviceCategory.some((cc) =>
    cc.coding?.some(
      (c) =>
        c.code === PRONTO_ATENDIMENTO_SERVICE_CATEGORY.code ||
        (c.system === PRONTO_ATENDIMENTO_SERVICE_CATEGORY.system &&
          c.code === PRONTO_ATENDIMENTO_SERVICE_CATEGORY.code),
    ),
  );
}

function getExt(schedule: fhir5.Schedule, url: string) {
  return schedule.extension?.find((e) => e.url === url);
}

export function mapScheduleToConfig(
  schedule: fhir5.Schedule,
  locationsById?: Map<string, fhir5.Location>,
): ProntoAtendimentoConfig | null {
  if (!schedule.id) return null;

  const attendanceExt = getExt(schedule, SCHEDULE_ATTENDANCE_TYPE_EXTENSION_URL);
  const costExt = getExt(schedule, SERVICE_COST_EXTENSION_URL);
  const defaultExt = getExt(schedule, PRONTO_ATENDIMENTO_DEFAULT_EXTENSION_URL);

  const locationActor = schedule.actor?.find((a) =>
    a.reference?.includes("Location/"),
  );
  const locationId = locationActor?.reference?.split("/")?.[1];
  const locationFromInclude = locationId
    ? locationsById?.get(locationId)
    : undefined;

  const hcsActor = schedule.actor?.find((a) =>
    a.reference?.includes("HealthcareService/"),
  );
  const healthcareServiceId = hcsActor?.reference?.split("/")?.[1];
  const healthcareServiceDisplay = hcsActor?.display;

  return {
    id: schedule.id,
    label: schedule.name || "Pronto Atendimento",
    mode: attendanceTypeToMode(attendanceExt?.valueCode),
    value: Number(costExt?.valueDecimal ?? 0),
    // Disponibilidade = Schedule.active (regra 1 agenda + switch)
    active: schedule.active !== false,
    isDefault: defaultExt?.valueBoolean === true,
    locationId,
    locationDisplay:
      locationActor?.display || locationFromInclude?.name || undefined,
    healthcareServiceId,
    healthcareServiceDisplay,
    specialty: healthcareServiceDisplay,
  };
}

/** Extrai Schedules e Locations de um Bundle (com _include). */
export function extractSchedulesAndLocations(bundle: fhir5.Bundle | undefined): {
  schedules: fhir5.Schedule[];
  locationsById: Map<string, fhir5.Location>;
} {
  const locationsById = new Map<string, fhir5.Location>();
  const schedules: fhir5.Schedule[] = [];

  bundle?.entry?.forEach((entry) => {
    const res = entry.resource;
    if (!res) return;
    if (res.resourceType === "Location" && res.id) {
      locationsById.set(res.id, res as fhir5.Location);
    }
    if (res.resourceType === "Schedule") {
      schedules.push(res as fhir5.Schedule);
    }
  });

  return { schedules, locationsById };
}
